// Canonical Snapshot 2.0 State/Direction model and attention/celebrate rules.
//
// This is the single frontend source of truth, mirroring the backend module
// `backend/snapshots/flags.py`. Keep the two in sync.

export const SCHEMA_LEGACY = 1; // 1-10 numeric score (read-only historical data)
export const SCHEMA_STATE_DIRECTION = 2; // State (1-4) + Direction (1-3)

// State scale (1-4). 3 is the healthy midpoint.
export const STATE_BREAKING = 1;
export const STATE_STRAINED = 2;
export const STATE_HEALTHY = 3;
export const STATE_THRIVING = 4;

export type StateValue = 1 | 2 | 3 | 4;
export type DirectionValue = 1 | 2 | 3;

export const STATE_LABELS: Record<number, string> = {
  [STATE_BREAKING]: 'Breaking',
  [STATE_STRAINED]: 'Strained',
  [STATE_HEALTHY]: 'Healthy',
  [STATE_THRIVING]: 'Thriving',
};

export const STATE_DESCRIPTIONS: Record<number, string> = {
  [STATE_BREAKING]: 'In crisis or near collapse — this area is breaking down.',
  [STATE_STRAINED]: 'Under real strain and not where it should be.',
  [STATE_HEALTHY]: 'Healthy — meeting your own definition of what healthy looks like here.',
  [STATE_THRIVING]: 'Thriving — flourishing well beyond the baseline.',
};

// Direction scale (1-3).
export const DIRECTION_SLIPPING = 1;
export const DIRECTION_MAINTAINING = 2;
export const DIRECTION_GROWING = 3;

export const DIRECTION_LABELS: Record<number, string> = {
  [DIRECTION_SLIPPING]: 'Slipping',
  [DIRECTION_MAINTAINING]: 'Maintaining',
  [DIRECTION_GROWING]: 'Growing',
};

// Entrepreneur Pulse (snapshot-level).
export type OwnerExperience = 'thriving' | 'steady' | 'drained';

export const OWNER_EXPERIENCE_OPTIONS: { value: OwnerExperience; label: string }[] = [
  { value: 'thriving', label: 'Thriving / Energized' },
  { value: 'steady', label: 'Steady / Okay' },
  { value: 'drained', label: 'Drained / Struggling' },
];

export const OWNER_EXPERIENCE_LABELS: Record<string, string> = Object.fromEntries(
  OWNER_EXPERIENCE_OPTIONS.map((o) => [o.value, o.label]),
);

export const MEMBER_PULSE_PROMPT = 'How are you experiencing being a business owner right now?';
export const OVERALL_PULSE_PROMPT = 'How are you doing overall?';

/** Opening Pulse line. Members keep the business-owner question. */
export function pulsePrompt(snapshotType: string | null | undefined): string {
  if (snapshotType === 'advisor' || snapshotType === 'leader' || snapshotType === 'nonprofit') {
    return OVERALL_PULSE_PROMPT;
  }
  return MEMBER_PULSE_PROMPT;
}

export function stateLabel(state?: number | null): string {
  return state != null ? STATE_LABELS[state] ?? '' : '';
}

export function directionLabel(direction?: number | null): string {
  return direction != null ? DIRECTION_LABELS[direction] ?? '' : '';
}

export function stateDescription(state?: number | null): string {
  return state != null ? STATE_DESCRIPTIONS[state] ?? '' : '';
}

export function isAttention(state?: number | null, direction?: number | null): boolean {
  return state === STATE_BREAKING || state === STATE_STRAINED || direction === DIRECTION_SLIPPING;
}

export function isCelebrate(state?: number | null, direction?: number | null): boolean {
  return state === STATE_THRIVING || direction === DIRECTION_GROWING;
}

export interface RatingFlags {
  attention: boolean;
  celebrate: boolean;
}

export function ratingFlags(
  state?: number | null,
  direction?: number | null,
  skipped = false,
): RatingFlags {
  if (skipped) return { attention: false, celebrate: false };
  return {
    attention: isAttention(state, direction),
    celebrate: isCelebrate(state, direction),
  };
}

// Ordering bucket for review/detail:
// attention (0) → celebrate (1) → remaining (2) → skipped (3).
export function ratingSortKey(
  state?: number | null,
  direction?: number | null,
  skipped = false,
): number {
  if (skipped) return 3;
  if (isAttention(state, direction)) return 0;
  if (isCelebrate(state, direction)) return 1;
  return 2;
}

// Tailwind color helper for a State value (used by v2 visuals).
export function stateColor(state?: number | null): string {
  switch (state) {
    case STATE_BREAKING:
      return 'bg-red-500';
    case STATE_STRAINED:
      return 'bg-orange-500';
    case STATE_HEALTHY:
      return 'bg-blue-500';
    case STATE_THRIVING:
      return 'bg-green-500';
    default:
      return 'bg-gray-400';
  }
}

// Tailwind color helper for a Direction value (used by controls and badges).
export function directionColor(direction?: number | null): string {
  switch (direction) {
    case DIRECTION_SLIPPING:
      return 'bg-red-500';
    case DIRECTION_MAINTAINING:
      return 'bg-gold';
    case DIRECTION_GROWING:
      return 'bg-green-500';
    default:
      return 'bg-gray-400';
  }
}
